// Deterministic Cascade quick-actions for the thread workspace. The three chips
// (Book / Add note / Add vehicle) open a scoped mini-form that hits the
// /api/dashboard/* fast-lane endpoints (NOT Eric), scoped to THIS thread's
// customer, and records the result into the transcript with a one-tap Undo —
// identical live and on reload. The thread's freeform Eric composer is separate.
//
// Reuses the deterministic endpoints + the existing ResultCard renderer; the
// only new UI is the scoped forms + the SlotPicker. Motion is restrained and
// reduced-motion-safe (emil-design-eng); cards are full-border, never side-
// striped, on the home-surface tokens (impeccable) — matching the rest of v2.

import { useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Calendar, Car, NotebookPen, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { AuthFetchError } from '@/lib/api'
import { decodeVin, isValidVin } from '@/lib/vin'
import {
  addVehicleForThread,
  appendNoteForThread,
  bookForThread,
  fetchAvailableSlots,
  threadKeys,
  type ChatVM,
  type NoteTarget,
  type SlotOption,
} from '@/lib/threads'
import { SlotPicker } from './SlotPicker'

type Verb = 'book' | 'note' | 'vehicle'

export function ThreadCascade({
  threadId,
  customerId,
  customerName,
  vehicleId,
  vehicleLabel,
  hasVehicle,
  onCommitted,
}: {
  threadId: string
  customerId: string
  customerName: string
  vehicleId: string | null
  vehicleLabel: string | null
  hasVehicle: boolean
  onCommitted: (vms: ChatVM[]) => void
}) {
  const { t } = useTranslation('dashboard')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  const [verb, setVerb] = useState<Verb | null>(null)
  const seqRef = useRef(0)

  const reset = () => setVerb(null)

  // One place to commit a finished action into the transcript, mirroring the
  // Eric onDone path (a user summary + the ✓ result card). Summary is built from
  // the RESPONSE so it byte-matches what the worker persisted (→ reload-identical).
  const commit = (summary: string, intent: string, result: { id: string; label: string; url: string }, requestId: string | null) => {
    const n = seqRef.current++
    onCommitted([
      { kind: 'user', id: `c${n}-user`, text: summary },
      {
        kind: 'result',
        id: `c${n}-result`,
        result,
        intent,
        requestId,
        undoable: !!requestId,
        undoneAt: null,
        createdAt: new Date().toISOString(),
      },
    ])
    queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
    reset()
  }

  if (!verb) {
    return (
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <CascadeChip icon={<Calendar className="size-3.5" />} label={t('home_v2.chip_book')} onClick={() => setVerb('book')} />
        <CascadeChip icon={<NotebookPen className="size-3.5" />} label={t('home_v2.chip_note')} onClick={() => setVerb('note')} />
        <CascadeChip icon={<Car className="size-3.5" />} label={t('home_v2.chip_vehicle')} onClick={() => setVerb('vehicle')} />
      </div>
    )
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-1 rounded-xl border border-border bg-card p-3 duration-200 ease-out motion-reduce:animate-none">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">
          {verb === 'book' && t('home_v2.chip_book')}
          {verb === 'note' && t('home_v2.chip_note')}
          {verb === 'vehicle' && t('home_v2.chip_vehicle')}
          <span className="ml-1.5 font-normal text-muted-foreground">· {customerName}</span>
        </span>
        <button
          type="button"
          onClick={reset}
          aria-label={t('home_v2.cascade_cancel')}
          className="flex size-7 items-center justify-center rounded-lg text-muted-foreground transition hover:text-foreground active:scale-[0.95]"
        >
          <X className="size-4" />
        </button>
      </div>

      {verb === 'book' && (
        <BookForm
          threadId={threadId}
          customerId={customerId}
          enabled={isLoaded}
          getToken={getToken}
          onCommit={commit}
          t={t}
        />
      )}
      {verb === 'note' && (
        <NoteForm
          threadId={threadId}
          customerId={customerId}
          vehicleId={vehicleId}
          vehicleLabel={vehicleLabel}
          hasVehicle={hasVehicle}
          getToken={getToken}
          onCommit={commit}
          t={t}
        />
      )}
      {verb === 'vehicle' && (
        <VehicleForm
          threadId={threadId}
          customerId={customerId}
          getToken={getToken}
          onCommit={commit}
          t={t}
        />
      )}
    </div>
  )
}

type GetToken = () => Promise<string | null>
type TFn = (key: string, opts?: Record<string, unknown>) => string
type CommitFn = (
  summary: string,
  intent: string,
  result: { id: string; label: string; url: string },
  requestId: string | null,
) => void

function CascadeChip({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="chip"
      size="pill-chip"
      onClick={onClick}
      className="h-auto gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      {icon}
      {label}
    </Button>
  )
}

function SendButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <Button
      type="button"
      variant="accent"
      size="pill-sm"
      onClick={onClick}
      disabled={disabled}
      className="px-3.5 text-sm font-medium disabled:opacity-40"
    >
      <ArrowUp className="size-4" />
      {label}
    </Button>
  )
}

function ErrorNote({ text }: { text: string }) {
  return <p className="mt-2 text-xs text-destructive">{text}</p>
}

// ── Book ─────────────────────────────────────────────────────────────────────
function BookForm({
  threadId,
  customerId,
  enabled,
  getToken,
  onCommit,
  t,
}: {
  threadId: string
  customerId: string
  enabled: boolean
  getToken: GetToken
  onCommit: CommitFn
  t: TFn
}) {
  const [slot, setSlot] = useState<SlotOption | null>(null)
  const [notes, setNotes] = useState('')

  const slotsQuery = useQuery({
    queryKey: ['thread-available-slots', threadId],
    enabled,
    queryFn: () => fetchAvailableSlots(getToken, { durationMin: 60, limit: 6 }),
    staleTime: 30_000,
  })

  const book = useMutation({
    mutationFn: () =>
      bookForThread(getToken, {
        customerId,
        startsAtIso: slot!.starts_at_iso,
        slotLabel: slot!.label,
        notes: notes.trim() || null,
        threadId,
      }),
    onSuccess: (data) => {
      const summary = `Book ${data.customer.name} · ${slot!.label}`
      onCommit(summary, 'book_appointment', { id: data.id, label: data.customer.name, url: data.url }, data.request_id)
    },
  })

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('home_v2.cascade_pick_time')}
      </p>
      <SlotPicker
        slots={slotsQuery.data?.slots ?? []}
        selectedIso={slot?.starts_at_iso ?? null}
        onSelect={setSlot}
        loading={slotsQuery.isLoading}
        emptyLabel={t('home_v2.cascade_no_slots')}
      />
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder={t('home_v2.cascade_book_notes_ph')}
        className="mt-3 min-h-0 resize-none border-border bg-background px-3 text-base leading-relaxed text-foreground focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
      />
      <div className="mt-3 flex justify-end">
        <SendButton
          onClick={() => book.mutate()}
          disabled={!slot || book.isPending}
          label={book.isPending ? t('home_v2.cascade_booking') : t('home_v2.cascade_send')}
        />
      </div>
      {book.isError && <ErrorNote text={t('home_v2.cascade_failed')} />}
    </div>
  )
}

// ── Add note ─────────────────────────────────────────────────────────────────
function NoteForm({
  threadId,
  customerId,
  vehicleId,
  vehicleLabel,
  hasVehicle,
  getToken,
  onCommit,
  t,
}: {
  threadId: string
  customerId: string
  vehicleId: string | null
  vehicleLabel: string | null
  hasVehicle: boolean
  getToken: GetToken
  onCommit: CommitFn
  t: TFn
}) {
  // Default to the vehicle (the work context) when one exists, else the customer.
  const [target, setTarget] = useState<NoteTarget>(hasVehicle ? 'vehicle' : 'customer')
  const [text, setText] = useState('')

  const note = useMutation({
    mutationFn: () =>
      appendNoteForThread(getToken, {
        kind: target,
        id: target === 'vehicle' ? vehicleId! : customerId,
        value: text.trim(),
        threadId,
      }),
    onSuccess: (data) => {
      onCommit(`Add note · ${data.label}`, 'update_field', { id: data.id, label: data.label, url: data.url }, data.request_id)
    },
  })

  return (
    <div>
      <div className="mb-2 inline-flex rounded-lg border border-border p-0.5">
        <TargetToggle
          active={target === 'vehicle'}
          disabled={!hasVehicle}
          onClick={() => setTarget('vehicle')}
          label={t('home_v2.cascade_note_target_vehicle')}
        />
        <TargetToggle
          active={target === 'customer'}
          disabled={false}
          onClick={() => setTarget('customer')}
          label={t('home_v2.cascade_note_target_customer')}
        />
      </div>
      {target === 'vehicle' && vehicleLabel && (
        <p className="mb-2 truncate font-mono text-xs text-muted-foreground">{vehicleLabel}</p>
      )}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        autoFocus
        placeholder={t('home_v2.cascade_note_ph')}
        className="min-h-0 resize-none border-border bg-background px-3 text-base leading-relaxed text-foreground focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
      />
      <div className="mt-3 flex justify-end">
        <SendButton
          onClick={() => note.mutate()}
          disabled={!text.trim() || note.isPending}
          label={note.isPending ? t('home_v2.cascade_adding') : t('home_v2.cascade_send')}
        />
      </div>
      {note.isError && <ErrorNote text={t('home_v2.cascade_failed')} />}
    </div>
  )
}

function TargetToggle({ active, disabled, onClick, label }: { active: boolean; disabled: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`rounded-md px-3 py-1 text-xs font-medium transition active:scale-[0.97] disabled:opacity-40 ${
        active ? 'bg-hv-accent-soft text-hv-accent' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

// ── Add vehicle ──────────────────────────────────────────────────────────────
function VehicleForm({
  threadId,
  customerId,
  getToken,
  onCommit,
  t,
}: {
  threadId: string
  customerId: string
  getToken: GetToken
  onCommit: CommitFn
  t: TFn
}) {
  const [vin, setVin] = useState('')
  const [year, setYear] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [decoding, setDecoding] = useState(false)
  const [dupe, setDupe] = useState<{ label: string; url: string } | null>(null)

  const onVinChange = async (raw: string) => {
    const next = raw.toUpperCase().replace(/\s/g, '')
    setVin(next)
    if (next.length === 17 && isValidVin(next)) {
      setDecoding(true)
      try {
        const decoded = await decodeVin(next)
        if (decoded.year) setYear(String(decoded.year))
        if (decoded.make) setMake(decoded.make)
        if (decoded.model) setModel(decoded.model)
      } catch {
        // leave fields for manual entry
      } finally {
        setDecoding(false)
      }
    }
  }

  const add = useMutation({
    mutationFn: () =>
      addVehicleForThread(getToken, {
        customerId,
        year: year.trim() ? Number(year.trim()) : null,
        make: make.trim() || null,
        model: model.trim() || null,
        vin: vin.trim() || null,
        threadId,
      }),
    onSuccess: (data) => {
      onCommit(`Add vehicle · ${data.vehicle.label}`, 'add_vehicle_for_customer', data.vehicle, data.request_id)
    },
    onError: (err) => {
      if (err instanceof AuthFetchError && err.status === 409) {
        const body = err.body as { existing?: { label: string; url: string } } | null
        if (body?.existing) setDupe({ label: body.existing.label, url: body.existing.url })
      }
    },
  })

  const canSubmit = !!(year.trim() || make.trim() || model.trim() || vin.trim()) && !add.isPending

  return (
    <div>
      <Input
        value={vin}
        onChange={(e) => void onVinChange(e.target.value)}
        maxLength={17}
        placeholder={t('home_v2.cascade_vehicle_vin_ph')}
        className="mb-2 h-auto border-border bg-background px-3 py-2 font-mono text-base uppercase text-foreground placeholder:font-sans placeholder:normal-case focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
      />
      {decoding && <p className="mb-2 text-xs text-muted-foreground">{t('home_v2.cascade_decoding')}</p>}
      <div className="grid grid-cols-3 gap-2">
        <Input
          value={year}
          onChange={(e) => setYear(e.target.value)}
          inputMode="numeric"
          placeholder={t('home_v2.cascade_vehicle_year_ph')}
          className="h-auto border-border bg-background px-3 py-2 text-base text-foreground focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
        />
        <Input
          value={make}
          onChange={(e) => setMake(e.target.value)}
          placeholder={t('home_v2.cascade_vehicle_make_ph')}
          className="h-auto border-border bg-background px-3 py-2 text-base text-foreground focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
        />
        <Input
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder={t('home_v2.cascade_vehicle_model_ph')}
          className="h-auto border-border bg-background px-3 py-2 text-base text-foreground focus-visible:border-muted-foreground/40 focus-visible:ring-0 md:text-base dark:bg-background"
        />
      </div>
      <div className="mt-3 flex justify-end">
        <SendButton
          onClick={() => add.mutate()}
          disabled={!canSubmit}
          label={add.isPending ? t('home_v2.cascade_adding') : t('home_v2.cascade_send')}
        />
      </div>
      {dupe ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('home_v2.cascade_vehicle_exists', { label: dupe.label })}{' '}
          <a href={dupe.url} className="font-medium text-hv-accent hover:underline">
            {t('home_v2.open')}
          </a>
        </p>
      ) : add.isError && !(add.error instanceof AuthFetchError && add.error.status === 409) ? (
        <ErrorNote text={t('home_v2.cascade_failed')} />
      ) : null}
    </div>
  )
}
